import { useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { ColDef } from 'ag-grid-community';
import { FieldSettingsT } from 'routes/BudgetModule/config/types';

const DEBOUNCE_TIME = 2000;

type UseColumnSettingsReturn = {
    columnsSettings: FieldSettingsT[];
    updateSettings: (colDefs: ColDef[]) => void;
};

/**
 * Hook to manage column settings with debounced updates.
 *
 * @param onSettingsUpdate - Callback triggered when column settings are updated.
 * @param initialSettings - Initial column settings, if any.
 * @returns The current column settings and a function to update them.
 */
export const useColumnSettings = (
    onSettingsUpdate: (settings: FieldSettingsT[]) => void,
    initialSettings: FieldSettingsT[] = [],
): UseColumnSettingsReturn => {
    const [columnsSettings, setColumnsSettings] =
        useState<FieldSettingsT[]>(initialSettings);

    const mutateSettings = useMemo(
        () => debounce(onSettingsUpdate, DEBOUNCE_TIME),
        [onSettingsUpdate],
    );

    const updateSettings = (colDefs: ColDef[]): void => {
        const newSettings: FieldSettingsT[] = colDefs.map((colDef, index) => ({
            field: colDef.field || '',
            hidden: !!colDef.hide,
            index,
            grouped: !!colDef.rowGroup,
            width: colDef.width === undefined ? 100 : colDef.width,
            groupIndex: colDef.rowGroupIndex ?? null,
        }));
        setColumnsSettings(newSettings);
        mutateSettings(newSettings);
    };

    return { columnsSettings, updateSettings };
};
